import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MultiCheckbox from "@/components/MultiCheckbox";
import FileUpload from "@/components/FileUpload";
import { SUBJECTS, CLASS_LEVELS, LESSON_TYPES } from "@/constants/options";

export default function TeacherProfileSetup() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    photo_path: "",
    yks_doc_path: "",
    university: "",
    department: "",
    age: "",
    subjects: [],
    class_levels: [],
    active_students: "",
    available_slots: "",
    lesson_types: [],
    intro: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || "" }));
    (async () => {
      try {
        const { data } = await api.get("/teacher/profile");
        if (data.profile) {
          setForm({
            name: data.profile.name || user?.name || "",
            photo_path: data.profile.photo_path || "",
            yks_doc_path: data.profile.yks_doc_path || "",
            university: data.profile.university || "",
            department: data.profile.department || "",
            age: data.profile.age || "",
            subjects: data.profile.subjects || [],
            class_levels: data.profile.class_levels || [],
            active_students: data.profile.active_students ?? "",
            available_slots: data.profile.available_slots ?? "",
            lesson_types: data.profile.lesson_types || [],
            intro: data.profile.intro || "",
          });
        }
      } catch {}
    })();
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subjects.length) return toast.error("En az bir branş seç");
    if (!form.class_levels.length) return toast.error("En az bir sınıf seviyesi seç");
    if (!form.lesson_types.length) return toast.error("En az bir ders türü seç");
    setLoading(true);
    try {
      await api.post("/teacher/profile", {
        ...form,
        age: parseInt(form.age) || 0,
        active_students: parseInt(form.active_students) || 0,
        available_slots: parseInt(form.available_slots) || 0,
      });
      await refresh();
      toast.success("Profilin kaydedildi");
      nav("/ogretmen/profil");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="reveal">
          <span className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Öğretmen kurulumu</span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="teacher-setup-title">
            Profilini oluştur
          </h1>
          <p className="mt-3 text-neutral-500">Aileler seni bulabilsin diye biraz kendinden bahset.</p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-10 reveal reveal-delay-1">
          <section className="space-y-5">
            <SectionTitle>Kişisel bilgiler</SectionTitle>
            <Field label="Ad Soyad">
              <Input required data-testid="tsetup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Yaş">
                <Input required type="number" min={16} max={90} data-testid="tsetup-age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="h-12" />
              </Field>
              <Field label="Üniversite / Mezuniyet">
                <Input required data-testid="tsetup-university" value={form.university} onChange={(e) => setForm({ ...form, university: e.target.value })} className="h-12" />
              </Field>
            </div>
            <Field label="Bölüm (Örn: Matematik Öğretmenliği, İnşaat Mühendisliği)">
              <Input required data-testid="tsetup-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="h-12" placeholder="Bölümünü yaz" />
            </Field>
            <FileUpload
              label="Profil fotoğrafı"
              accept="image/*"
              value={form.photo_path}
              onChange={(p) => setForm({ ...form, photo_path: p })}
              testid="tsetup-photo-upload"
            />
            <FileUpload
              label="YKS sonuç belgesi (fotoğraf)"
              accept="image/*"
              value={form.yks_doc_path}
              onChange={(p) => setForm({ ...form, yks_doc_path: p })}
              testid="tsetup-yks-upload"
            />
          </section>

          <section className="space-y-5">
            <SectionTitle>Ders verdiğin branşlar</SectionTitle>
            <MultiCheckbox
              options={SUBJECTS}
              values={form.subjects}
              onChange={(v) => setForm({ ...form, subjects: v })}
              testidPrefix="tsetup-subject"
            />
          </section>

          <section className="space-y-5">
            <SectionTitle>Sınıf seviyeleri</SectionTitle>
            <MultiCheckbox
              options={CLASS_LEVELS}
              values={form.class_levels}
              onChange={(v) => setForm({ ...form, class_levels: v })}
              testidPrefix="tsetup-class"
            />
          </section>

          <section className="space-y-5">
            <SectionTitle>Ders türü</SectionTitle>
            <MultiCheckbox
              options={LESSON_TYPES}
              values={form.lesson_types}
              onChange={(v) => setForm({ ...form, lesson_types: v })}
              testidPrefix="tsetup-lesson"
            />
          </section>

          <section className="space-y-5">
            <SectionTitle>Kapasite</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Şu an aktif öğrenci sayın">
                <Input required type="number" min={0} data-testid="tsetup-active" value={form.active_students} onChange={(e) => setForm({ ...form, active_students: e.target.value })} className="h-12" />
              </Field>
              <Field label="Kaç öğrenciye daha müsaitsin">
                <Input required type="number" min={0} data-testid="tsetup-available" value={form.available_slots} onChange={(e) => setForm({ ...form, available_slots: e.target.value })} className="h-12" />
              </Field>
            </div>
          </section>

          <section className="space-y-5">
            <SectionTitle>Kendini tanıt</SectionTitle>
            <Textarea
              required
              rows={5}
              data-testid="tsetup-intro"
              value={form.intro}
              onChange={(e) => setForm({ ...form, intro: e.target.value })}
              placeholder="Eğitim geçmişin, öğretme yaklaşımın, öğrencilerinle nasıl çalıştığın hakkında birkaç cümle..."
              className="rounded-lg border-neutral-300 focus:border-neutral-900"
            />
          </section>

          <Button type="submit" disabled={loading} data-testid="tsetup-submit"
            className="w-full h-12 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white text-base shadow-md shadow-blue-500/20">
            {loading ? "Kaydediliyor..." : "Profili Kaydet"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-display text-xl font-semibold tracking-tight">{children}</h2>;
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
